import { SearchQuery, encodeSearchObject } from '@sparted/shared-library';
import type { LanguageCode } from '@sparted/shared-library/business/types';

import { fetchFactory } from 'Libs/http/utils';
import { createAsyncAction } from 'Libs/redux/utils';

import { ContentFromApi } from '../content/content.types';
import type {
  BreakingNews,
  Campaign,
  CampaignFromApi,
  SearchActivitiesResponse,
  SearchActivityArgs,
} from './activity.type';

export const searchActivities = createAsyncAction<SearchActivitiesResponse, SearchActivityArgs>(
  'activity/search',
  (args) => {
    const { offset, limit, query = {} } = args;

    const { fetchFn } = fetchFactory<SearchActivitiesResponse>({
      route: `/api/back-office/activities?${new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
        ...Object.entries(query).reduce<Record<string, string>>((acc, [key, value]) => {
          if (value) {
            acc[key] = value.toString();
          }

          return acc;
        }, {}),
      }).toString()}`,
      method: 'GET',
    });
    return fetchFn();
  },
);

export const fetchCampaignById = createAsyncAction('activity/campaign/fetch', async (id: number) => {
  const { fetchFn } = fetchFactory<CampaignFromApi>({
    route: `/api/campaigns/${id}`,
    method: 'GET',
  });

  return fetchFn();
});

export type CampaignArgs = Pick<Campaign, 'informations' | 'scheduling' | 'languages' | 'status'>;

export type BreakingNewsArgs = Pick<BreakingNews, 'informations' | 'languages'>;

export const createCampaign = createAsyncAction<{ id: number }, CampaignArgs>(
  'activity/campaign/create',
  async (args) => {
    const { fetchFn } = fetchFactory<Campaign>({
      route: '/api/campaigns',
      method: 'POST',
      payload: args,
    });

    return fetchFn();
  },
);

export const saveCampaign = createAsyncAction<CampaignFromApi, CampaignArgs & { id: number }>(
  'activity/campaign/save',
  async (args) => {
    const { fetchFn } = fetchFactory<CampaignFromApi>({
      route: `/api/campaigns/${args.id}`,
      method: 'PUT',
      payload: args,
    });

    return fetchFn();
  },
);

type RequestCampaignContentsTranslationArgs = {
  contentIds: number[];
  languageCodes: LanguageCode[];
};
export const requestCampaignContentsTranslation = createAsyncAction<
  ContentFromApi[],
  RequestCampaignContentsTranslationArgs
>('activity/campaign/contents/requestTranslation', async ({ contentIds, languageCodes }) => {
  const { fetchFn } = fetchFactory<ContentFromApi[]>({
    route: `/api/contents/translation/`,
    method: 'POST',
    payload: {
      contentIds,
      languageCodes,
    },
  });
  return fetchFn();
});

export type BreakingNewsRetrieveResponse = BreakingNews;

export const createBreakingNews = createAsyncAction<{ id: number }, BreakingNewsArgs>(
  'activity/breakingNews/create',
  async (args) => {
    const { fetchFn } = fetchFactory<BreakingNewsRetrieveResponse>({
      route: '/api/breaking-news',
      method: 'POST',
      payload: args,
    });

    return fetchFn();
  },
);

export const fetchBreakingNewsById = createAsyncAction('activity/breakingNews/fetch', async (id: number) => {
  const { fetchFn } = fetchFactory<BreakingNewsRetrieveResponse>({
    route: `/api/breaking-news/${id}`,
    method: 'GET',
  });

  return fetchFn();
});

export const updateBreakingNews = createAsyncAction<BreakingNewsRetrieveResponse, Partial<BreakingNews>>(
  'activity/breakingNews/update',
  async (args) => {
    const { fetchFn } = fetchFactory<BreakingNewsRetrieveResponse>({
      route: `/api/breaking-news/${args.id}`,
      method: 'PUT',
      payload: args,
    });

    return fetchFn();
  },
);

// Archive activity
export const archiveActivity = createAsyncAction<{ id: number }, { id: number }>('activity/archive', async (args) => {
  const { fetchFn } = fetchFactory<{ id: number }>({
    route: `/api/back-office/activities/${args.id}/archive`,
    method: 'PUT',
  });

  return fetchFn();
});

// Archive activity
export const unarchiveActivity = createAsyncAction<{ id: number }, { id: number }>(
  'activity/unarchive',
  async (args) => {
    const { fetchFn } = fetchFactory<{ id: number }>({
      route: `/api/back-office/activities/${args.id}/unarchive`,
      method: 'PUT',
    });

    return fetchFn();
  },
);
